import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  FileCode, DollarSign, TrendingUp, TrendingDown, Upload,
  AlertTriangle, CheckCircle, Loader2, ChevronRight, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TerraformCostPreview = () => {
  const { shouldShowData } = useDemoData();

  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [hclContent, setHclContent] = useState('');
  const [planJson, setPlanJson] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [pricingRef, setPricingRef] = useState(null);

  const exampleHCL = `resource "aws_instance" "web" {
  ami           = "ami-0c55b159cbfafe1f0"
  instance_type = "t3.medium"
  
  tags = {
    Name = "WebServer"
  }
}

resource "aws_rds_instance" "database" {
  allocated_storage    = 100
  engine              = "mysql"
  engine_version      = "8.0"
  instance_class      = "db.t3.medium"
  db_name             = "mydb"
  skip_final_snapshot = true
}

resource "aws_s3_bucket" "storage" {
  bucket = "my-application-bucket"
}`;

  const analyzeHCL = async () => {
    if (!hclContent.trim()) {
      toast.error('Please enter Terraform HCL content');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/terraform/estimate-hcl`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ hcl_content: hclContent })
      });
      
      const data = await response.json();
      setAnalysisResult(data);
      toast.success('Analysis complete');
    } catch (error) {
      toast.error('Failed to analyze Terraform code');
    } finally {
      setLoading(false);
    }
  };

  const analyzePlan = async () => {
    if (!planJson.trim()) {
      toast.error('Please paste Terraform plan JSON');
      return;
    }
    
    let parsedPlan;
    try {
      parsedPlan = JSON.parse(planJson);
    } catch {
      toast.error('Invalid JSON format');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/terraform/analyze-plan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan_json: parsedPlan })
      });
      
      const data = await response.json();
      setAnalysisResult(data);
      toast.success('Plan analysis complete');
    } catch (error) {
      toast.error('Failed to analyze Terraform plan');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingReference = async () => {
    try {
      const response = await fetch(`${API_URL}/api/terraform/pricing-reference`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPricingRef(data);
    } catch (error) {
      toast.error('Failed to load pricing reference');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const loadExample = () => {
    setHclContent(exampleHCL);
    toast.success('Example loaded');
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Terraform Cost Preview" description="Connect your cloud account to see real data here." />;
  }


  return (
    <div className="space-y-6" data-testid="terraform-cost-preview">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileCode className="w-7 h-7 text-emerald-500" />
            Terraform Cost Preview
          </h1>
          <p className="text-slate-400 mt-1">Estimate infrastructure costs before deployment</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="hcl" className="space-y-4">
        <TabsList>
          <TabsTrigger value="hcl">Analyze HCL</TabsTrigger>
          <TabsTrigger value="plan">Analyze Plan JSON</TabsTrigger>
          <TabsTrigger value="pricing" onClick={fetchPricingReference}>Pricing Reference</TabsTrigger>
        </TabsList>

        {/* HCL Analysis Tab */}
        <TabsContent value="hcl" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Terraform Code</CardTitle>
                  <Button variant="outline" size="sm" onClick={loadExample}>
                    Load Example
                  </Button>
                </div>
                <CardDescription>
                  Paste your Terraform HCL code to estimate costs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="resource &quot;aws_instance&quot; &quot;web&quot; { ... }"
                  value={hclContent}
                  onChange={(e) => setHclContent(e.target.value)}
                  className="font-mono text-sm h-96"
                />
                <Button 
                  className="w-full mt-4" 
                  onClick={analyzeHCL}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-4 h-4 mr-2" />
                      Estimate Costs
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            <Card>
              <CardHeader>
                <CardTitle>Cost Estimate</CardTitle>
              </CardHeader>
              <CardContent>
                {analysisResult ? (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="p-4 bg-emerald-500 bg-gradient-to-br from-emerald-500 to-indigo-600 rounded-lg text-white">
                      <p className="text-cyan-100 text-sm">Estimated Monthly Cost</p>
                      <p className="text-3xl font-bold mt-1">
                        {formatCurrency(analysisResult.total_monthly_cost || analysisResult.summary?.net_monthly_change || 0)}
                      </p>
                      {analysisResult.total_annual_cost && (
                        <p className="text-cyan-200 text-sm mt-1">
                          {formatCurrency(analysisResult.total_annual_cost)}/year
                        </p>
                      )}
                    </div>

                    {/* Resources */}
                    {analysisResult.estimated_resources && (
                      <div>
                        <h4 className="font-medium mb-2">Resources</h4>
                        <div className="space-y-2">
                          {analysisResult.estimated_resources.map((resource, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <FileCode className="w-4 h-4 text-slate-400" />
                                <span>{resource.type}</span>
                                <Badge variant="outline">{resource.name}</Badge>
                              </div>
                              <span className="font-medium">{formatCurrency(resource.monthly_cost)}/mo</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Plan Summary */}
                    {analysisResult.summary && (
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center">
                          <p className="text-2xl font-bold text-emerald-600">{analysisResult.summary.resources_to_create}</p>
                          <p className="text-sm text-slate-400">To Create</p>
                        </div>
                        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                          <p className="text-2xl font-bold text-amber-600">{analysisResult.summary.resources_to_update}</p>
                          <p className="text-sm text-slate-400">To Update</p>
                        </div>
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                          <p className="text-2xl font-bold text-red-600">{analysisResult.summary.resources_to_destroy}</p>
                          <p className="text-sm text-slate-400">To Destroy</p>
                        </div>
                      </div>
                    )}

                    {/* Recommendations */}
                    {analysisResult.recommendations?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Cost Optimization Tips</h4>
                        <div className="space-y-2">
                          {analysisResult.recommendations.map((rec, idx) => (
                            <div key={idx} className="p-3 border border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-lg">
                              <div className="flex items-center justify-between">
                                <div>
                                  <Badge variant="outline" className="mb-1">{rec.type}</Badge>
                                  <p className="text-sm">{rec.description}</p>
                                </div>
                                <span className="text-emerald-600 font-medium">
                                  Save {formatCurrency(rec.potential_savings)}/mo
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {analysisResult.note && (
                      <p className="text-sm text-slate-400 italic">{analysisResult.note}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                    <FileCode className="w-12 h-12 mb-3" />
                    <p>Enter Terraform code and click "Estimate Costs"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Plan JSON Tab */}
        <TabsContent value="plan" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Terraform Plan JSON</CardTitle>
              <CardDescription>
                Run <code className="bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">terraform plan -json</code> and paste the output below
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder='{"format_version": "1.0", "resource_changes": [...]}'
                value={planJson}
                onChange={(e) => setPlanJson(e.target.value)}
                className="font-mono text-sm h-64"
              />
              <Button 
                className="w-full mt-4" 
                onClick={analyzePlan}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <DollarSign className="w-4 h-4 mr-2" />
                    Analyze Plan
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing Reference Tab */}
        <TabsContent value="pricing" className="space-y-4">
          {pricingRef ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.entries(pricingRef).map(([provider, categories]) => (
                <Card key={provider}>
                  <CardHeader>
                    <CardTitle className="capitalize">{provider}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Object.entries(categories).map(([category, prices]) => (
                      <div key={category} className="mb-4">
                        <h4 className="font-medium text-sm text-slate-400 mb-2 capitalize">
                          {category.replace('_', ' ')}
                        </h4>
                        <div className="space-y-1">
                          {Object.entries(prices).map(([instance, price]) => (
                            <div key={instance} className="flex justify-between text-sm">
                              <span className="font-mono">{instance}</span>
                              <span className="text-slate-400">${price}/hr</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="flex items-center justify-center h-48">
                <p className="text-slate-400">Loading pricing data...</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TerraformCostPreview;
